import { useApi } from "@platform/api";
import { logger } from "@platform/browser-logger";
import { queryKeys } from "@platform/plan-grid-core";
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";

import {
  downloadRfpFile,
  getDocumentsSummary,
  processDocument,
  removeDocument,
  renameDocument,
  removeDocumentCoverages,
  uploadDocuments,
} from "../api/client";

import { getPlanBuilderDataCacheKey } from "./usePlanBuilderData";

import type {
  DocumentLibraryStatsResponse,
  PlanBuilderResponse,
  ProcessDocumentResponse,
  RenameDocumentResponse,
  RfpDocumentsSummaryResponse,
  UploadDocumentsRequest,
  UploadDocumentsResponse,
} from "../api/types";

const DOCUMENT_LIBRARY_STATS_QUERY_KEY = "documentLibraryStats";

const mapDocumentsSummaryToStatsResponse = (
  summary: RfpDocumentsSummaryResponse,
): DocumentLibraryStatsResponse => ({
  total: summary.total,
  extracted: summary.extracted,
  processing: summary.processing,
  requiresReview: summary.requiresReview,
  excluded: summary.excluded,
  errorCount: summary.error,
  lastUpdated: new Date().toISOString(),
  error: null,
});

interface ProcessDocumentMutationVariables {
  rfpId: string;
  documentId: string;
  planEffectiveDate?: string;
}

interface RemoveDocumentMutationVariables {
  rfpId: string;
  documentId: string;
}

interface RenameDocumentMutationVariables {
  rfpId: string;
  documentId: string;
  newDocumentName: string;
}

interface RemoveDocumentCoverageMutationVariables {
  rfpId: string;
  documentId: string;
  coverages: { coverageType: string }[];
}

export const getDocumentLibraryStatsCacheKey = (rfpId: string): string[] => [
  DOCUMENT_LIBRARY_STATS_QUERY_KEY,
  rfpId,
];

// useDocumentLibraryStatsQuery fetches document processing statistics for a given RFP ID
// Auto-refreshes at the specified interval to reflect latest processing status
export function useDocumentLibraryStatsQuery({
  rfpId,
  refetchInterval,
}: {
  rfpId: string;
  refetchInterval: number;
}): UseQueryResult<DocumentLibraryStatsResponse, Error> {
  const axiosInstance = useApi();

  return useQuery<DocumentLibraryStatsResponse, Error>({
    queryKey: getDocumentLibraryStatsCacheKey(rfpId),
    queryFn: async () => {
      const response = await getDocumentsSummary(rfpId, axiosInstance);
      return mapDocumentsSummaryToStatsResponse(response);
    },
    enabled: !!rfpId,
    refetchInterval,
    refetchIntervalInBackground: false, // Only refetch when tab is active
  });
}

export function useUploadDocumentsMutation(): UseMutationResult<
  UploadDocumentsResponse,
  Error,
  UploadDocumentsRequest
> {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  return useMutation<UploadDocumentsResponse, Error, UploadDocumentsRequest>({
    mutationFn: async ({ rfpId, brokerId, brokerPortalRfpId, documents }) =>
      uploadDocuments(
        rfpId,
        brokerId,
        brokerPortalRfpId,
        documents,
        axiosInstance,
      ),
    onError: (error, variables) => {
      logger.error(
        `[Document Library] Upload mutation failed for rfpId=${variables.rfpId}`,
        error,
      );
    },
    onSuccess: async (_response, variables) => {
      logger.debug("[Document Library] Upload successful, invalidating stats", {
        rfpId: variables.rfpId,
      });
      // Only invalidate stats here — planBuilderData is invalidated by
      // handleUpload after all process-document calls have settled.
      await queryClient.invalidateQueries({
        queryKey: getDocumentLibraryStatsCacheKey(variables.rfpId),
      });
    },
  });
}

export function useDownloadRfpFileMutation(): UseMutationResult<
  Blob,
  Error,
  { downloadLink: string }
> {
  const axiosInstance = useApi();

  return useMutation<Blob, Error, { downloadLink: string }>({
    mutationFn: async ({ downloadLink }) =>
      downloadRfpFile(downloadLink, axiosInstance),
  });
}

interface OptimisticContext {
  previousStats: DocumentLibraryStatsResponse | undefined;
}

export function useProcessDocumentMutation(): UseMutationResult<
  ProcessDocumentResponse,
  Error,
  ProcessDocumentMutationVariables
> {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    ProcessDocumentResponse,
    Error,
    ProcessDocumentMutationVariables,
    OptimisticContext
  >({
    mutationFn: async ({
      rfpId,
      documentId,
      planEffectiveDate,
    }): Promise<ProcessDocumentResponse> =>
      (await processDocument(
        rfpId,
        documentId,
        axiosInstance,
        planEffectiveDate,
      )) as ProcessDocumentResponse,
    onMutate: async (variables) => {
      const statsKey = getDocumentLibraryStatsCacheKey(variables.rfpId);
      await queryClient.cancelQueries({ queryKey: statsKey });

      const previousStats =
        queryClient.getQueryData<DocumentLibraryStatsResponse>(statsKey);

      // Optimistically increment processing count in stats
      if (previousStats) {
        queryClient.setQueryData<DocumentLibraryStatsResponse>(statsKey, {
          ...previousStats,
          processing: previousStats.processing + 1,
        });
      }

      return { previousStats };
    },
    onError: (_error, variables, context) => {
      if (context?.previousStats) {
        queryClient.setQueryData(
          getDocumentLibraryStatsCacheKey(variables.rfpId),
          context.previousStats,
        );
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: getDocumentLibraryStatsCacheKey(variables.rfpId),
      });
      queryClient.invalidateQueries({
        queryKey: getPlanBuilderDataCacheKey(variables.rfpId),
      });
    },
  });
}

interface RemoveDocumentOptimisticContext {
  previousStats: DocumentLibraryStatsResponse | undefined;
  previousPlanBuilder: PlanBuilderResponse | undefined;
}

export function useRemoveDocumentMutation(): UseMutationResult<
  void,
  Error,
  RemoveDocumentMutationVariables
> {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    void,
    Error,
    RemoveDocumentMutationVariables,
    RemoveDocumentOptimisticContext
  >({
    mutationFn: async ({ rfpId, documentId }) =>
      removeDocument(rfpId, documentId, axiosInstance),
    onMutate: async (variables) => {
      const statsKey = getDocumentLibraryStatsCacheKey(variables.rfpId);
      const planBuilderKey = getPlanBuilderDataCacheKey(variables.rfpId);

      await queryClient.cancelQueries({ queryKey: statsKey });
      await queryClient.cancelQueries({ queryKey: planBuilderKey });

      const previousStats =
        queryClient.getQueryData<DocumentLibraryStatsResponse>(statsKey);
      const previousPlanBuilder =
        queryClient.getQueryData<PlanBuilderResponse>(planBuilderKey);

      // Optimistically remove the document from plan builder data
      if (previousPlanBuilder) {
        const removeDoc = (
          groups: PlanBuilderResponse["currentCoverages"],
        ): PlanBuilderResponse["currentCoverages"] =>
          groups.map((group) => ({
            ...group,
            documents: group.documents.filter(
              (doc) => doc.id !== variables.documentId,
            ),
          }));

        queryClient.setQueryData<PlanBuilderResponse>(planBuilderKey, {
          ...previousPlanBuilder,
          currentCoverages: removeDoc(previousPlanBuilder.currentCoverages),
          newCoverages: removeDoc(previousPlanBuilder.newCoverages),
          otherCoverages: removeDoc(previousPlanBuilder.otherCoverages),
        });
      }

      // Optimistically decrement total in stats
      if (previousStats) {
        queryClient.setQueryData<DocumentLibraryStatsResponse>(statsKey, {
          ...previousStats,
          total: Math.max(0, previousStats.total - 1),
        });
      }

      return { previousStats, previousPlanBuilder };
    },
    onError: (_error, variables, context) => {
      if (context?.previousStats) {
        queryClient.setQueryData(
          getDocumentLibraryStatsCacheKey(variables.rfpId),
          context.previousStats,
        );
      }
      if (context?.previousPlanBuilder) {
        queryClient.setQueryData(
          getPlanBuilderDataCacheKey(variables.rfpId),
          context.previousPlanBuilder,
        );
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: getDocumentLibraryStatsCacheKey(variables.rfpId),
      });
      queryClient.invalidateQueries({
        queryKey: getPlanBuilderDataCacheKey(variables.rfpId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.benefitClasses(variables.rfpId),
      });
    },
  });
}

export function useRemoveDocumentCoverageMutation(): UseMutationResult<
  void,
  Error,
  RemoveDocumentCoverageMutationVariables
> {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  return useMutation<void, Error, RemoveDocumentCoverageMutationVariables>({
    mutationFn: async ({ rfpId, documentId, coverages }) =>
      removeDocumentCoverages(rfpId, documentId, coverages, axiosInstance),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: getDocumentLibraryStatsCacheKey(variables.rfpId),
      });
      queryClient.invalidateQueries({
        queryKey: getPlanBuilderDataCacheKey(variables.rfpId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.benefitClasses(variables.rfpId),
      });
    },
  });
}

export function useRenameDocumentMutation(): UseMutationResult<
  RenameDocumentResponse,
  Error,
  RenameDocumentMutationVariables
> {
  const axiosInstance = useApi();
  const queryClient = useQueryClient();

  return useMutation<
    RenameDocumentResponse,
    Error,
    RenameDocumentMutationVariables
  >({
    mutationFn: async ({ rfpId, documentId, newDocumentName }) =>
      renameDocument(rfpId, documentId, newDocumentName, axiosInstance),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: getPlanBuilderDataCacheKey(variables.rfpId),
      });
    },
  });
}

export function useDownloadRfpFile(): {
  downloadFile: (downloadLink: string) => Promise<Blob>;
} {
  const downloadRfpFileMutation = useDownloadRfpFileMutation();

  return {
    downloadFile: (downloadLink: string): Promise<Blob> =>
      downloadRfpFileMutation.mutateAsync({ downloadLink }),
  };
}
